import type { AssetResponse, ColumnFilterMenuState } from '../types/assets';
import { ASSET_TABLE_COLUMNS } from '../types/assets';

/** Format ISO date string to dd-MMM-yyyy */
export const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
  } catch {
    return iso;
  }
};

/** Truncate text with ellipsis */
export const trimText = (text: string, max = 40): string =>
  text && text.length > max ? text.slice(0, max) + '…' : text || '';

/** Prefix URL with https:// if no protocol is present */
export const toHref = (url: string): string =>
  url.startsWith('http') ? url : `https://${url}`;

/** Email validation regex */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Plain text length from HTML (strip tags) for validation and previews */
export const getTextLengthFromHtml = (html: string): number => {
  if (!html || !html.trim()) return 0;
  const stripped = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return stripped.length;
};

/** Resolve a cell's display value for filtering */
export const getCellValue = (row: AssetResponse, columnId: string): string => {
  switch (columnId) {
    case 'asset_code': return row.asset_code || '';
    case 'asset_name': return row.asset_name || '';
    case 'asset_type': return row.asset_type || '';
    case 'gtm_category': return row.gtm_category || '';
    case 'submitted_by': return row.submitted_by || '';
    case 'submitted_at': return formatDate(row.submitted_at);
    case 'marketplace_listing_status': return row.marketplace_listing_status ? 'Yes' : 'No';
    case 'submitter_business_unit': return row.submitter_business_unit?.trim() || '';
    case 'submitter_sub_bu': return row.submitter_sub_bu?.trim() || '';
    default: return '';
  }
};

/**
 * Apply global search + column filters. When `excludeColumnId` is set, that
 * column's selections are ignored so its filter menu can show cascading values.
 */
export const applySearchAndColumnFilters = (
  list: AssetResponse[],
  searchTerm: string,
  menus: Record<string, ColumnFilterMenuState>,
  excludeColumnId?: string
): AssetResponse[] => {
  let result = list;

  if (searchTerm.trim()) {
    const q = searchTerm.trim().toLowerCase();
    result = result.filter(a =>
      (a.asset_code || '').toLowerCase().includes(q) ||
      a.asset_name.toLowerCase().includes(q) ||
      (a.asset_type || '').toLowerCase().includes(q) ||
      (a.gtm_category || '').toLowerCase().includes(q) ||
      a.submitted_by.toLowerCase().includes(q) ||
      (a.submitter_business_unit || '').toLowerCase().includes(q) ||
      (a.submitter_sub_bu || '').toLowerCase().includes(q) ||
      a.key_cloud_services.some(s => s.toLowerCase().includes(q))
    );
  }

  ASSET_TABLE_COLUMNS.forEach(col => {
    if (!col.filterable) return;
    if (excludeColumnId !== undefined && col.id === excludeColumnId) return;
    const selected = menus[col.id]?.selectedValues ?? [];
    if (selected.length === 0) return;
    if (col.id === 'key_cloud_services') {
      result = result.filter(row =>
        selected.some(s =>
          row.key_cloud_services.some(cs => cs.trim() === s)
        )
      );
    } else {
      result = result.filter(row => selected.includes(getCellValue(row, col.id)));
    }
  });

  return result;
};

export type AssetSortDirection = 'asc' | 'desc';

/** Sort a copy of the list by table column (used after search/filters). */
export const sortAssetsByColumn = (
  list: AssetResponse[],
  columnId: string,
  direction: AssetSortDirection
): AssetResponse[] => {
  const mul = direction === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    let cmp = 0;
    switch (columnId) {
      case 'asset_code':
        cmp = (a.asset_code || '').localeCompare(b.asset_code || '', undefined, { sensitivity: 'base' });
        break;
      case 'asset_name':
        cmp = (a.asset_name || '').localeCompare(b.asset_name || '', undefined, { sensitivity: 'base' });
        break;
      case 'asset_type':
        cmp = (a.asset_type || '').localeCompare(b.asset_type || '', undefined, { sensitivity: 'base' });
        break;
      case 'gtm_category':
        cmp = (a.gtm_category || '').localeCompare(b.gtm_category || '', undefined, { sensitivity: 'base' });
        break;
      case 'key_cloud_services': {
        const sa = [...a.key_cloud_services].map(s => s.trim()).filter(Boolean).sort().join(', ');
        const sb = [...b.key_cloud_services].map(s => s.trim()).filter(Boolean).sort().join(', ');
        cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base' });
        break;
      }
      case 'submitted_by':
        cmp = (a.submitted_by || '').localeCompare(b.submitted_by || '', undefined, { sensitivity: 'base' });
        break;
      case 'submitted_at': {
        const ta = new Date(a.submitted_at).getTime();
        const tb = new Date(b.submitted_at).getTime();
        cmp = (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
        break;
      }
      case 'marketplace_listing_status': {
        const va = a.marketplace_listing_status ? 1 : 0;
        const vb = b.marketplace_listing_status ? 1 : 0;
        cmp = va - vb;
        break;
      }
      case 'submitter_business_unit':
        cmp = (a.submitter_business_unit || '').localeCompare(
          b.submitter_business_unit || '',
          undefined,
          { sensitivity: 'base' },
        );
        break;
      case 'submitter_sub_bu':
        cmp = (a.submitter_sub_bu || '').localeCompare(
          b.submitter_sub_bu || '',
          undefined,
          { sensitivity: 'base' },
        );
        break;
      default:
        return 0;
    }
    return cmp * mul;
  });
};
